import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { Conditional } from 'common/components/layout';
import lodash from 'lodash';
import moment from 'moment';

import { Box, Divider } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { replaceTimezone, toISOLocalWithTz, getTzAbbreviation } from 'common/util/dateTime';

import styles from './AuditHistoryItem.scss';

const ACTIVITY_ADD_TEAM_MEMBER = 'A corresponding delegation is created when a Study Team Member is added in Study Settings.';
const ORIGIN_STUDY_SETTINGS = 'Study Settings';
const ORIGIN_DELEGATION = 'Delegation';

const getOriginDisplay = (data) => {
    const origin = lodash.get(data, 'value.origin', '');
    if (data.actionType.includes('SIGN')) {
        return origin || 'Inbox';
    }
    if (data.actionType.includes('CREATE')) {
        return origin || ORIGIN_STUDY_SETTINGS;
    }
    return origin || ORIGIN_DELEGATION;
};

const getActivityDisplay = (data) => {
    const activity = lodash.get(data, 'value.activity', '');
    if (data.actionType.includes('CREATE')) {
        return activity || ACTIVITY_ADD_TEAM_MEMBER;
    }
    return activity;
};

const useStyles = makeStyles((theme) => ({
    expand: {
        transform: 'rotate(0deg)',
        marginLeft: 'auto',
        cursor: 'pointer',
        color: 'rgba(0,86,173,1)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
}));

const scrollIntoView = (ref) => {
    if (lodash.isNil(ref) || !lodash.isFunction(ref.scrollIntoView)) {
        return;
    }

    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const parseDateFormat = (date) => {
    if (lodash.isNil(date)) {
        return null;
    }

    return moment.utc(date).format('MMM DD, YYYY');
};

export default function AuditHistoryItem({
    data,
    open,
    index,
    testament,
    isStudyType,
    onClickExpand,
    delegationName,
    institutionTimezone,
    institutionTimezoneName,
}) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(open);
    }, [open]);

    const handleExpandClick = () => {
        setExpanded(!expanded);
        onClickExpand(index);
    };

    const renderCreateAndSignAudit = () => {
        const orderDisplayData = [
            {
                label: 'ROLE:',
                key: 'value.roleName',
            },
            {
                label: 'TASKS:',
                key: 'value.tasks',
                render: () => {
                    return data.value.tasks.map((task, index) => {
                        return (
                            <Box key={index} marginBottom={1}>
                                {task.taskName}
                            </Box>
                        );
                    });
                },
            },
            {
                label: 'START DATE:',
                key: 'value.startDate',
                render: () => {
                    return (
                        <Box marginBottom={1}>
                            {parseDateFormat(data.value.startDate)}
                        </Box>
                    );
                },
            },
            {
                label: 'END DATE:',
                key: 'value.endDate',
                render: () => {
                    return (
                        <Box marginBottom={1}>
                            {parseDateFormat(data.value.endDate)}
                        </Box>
                    );
                },
            },
            {
                label: 'ORIGINAL SIGNATURES:',
                key: 'value.defaultSignatureExpectations',
                render: () => {
                    if (!data.value.defaultSignatureExpectations) return;
                    return data.value.defaultSignatureExpectations.map(
                        (originalSignature, index) => {
                            return (
                                <Box key={index} marginBottom={1}>
                                    {originalSignature.user.name}
                                </Box>
                            );
                        }
                    );
                },
            },
            {
                label: 'REQUESTED SIGNATURES:',
                key: 'value.signatureExpectations',
                render: () => {
                    if (!data.value.signatureExpectations) return;
                    return lodash
                        .uniqBy(data.value.signatureExpectations, 'user.name')
                        .map((signature, index) => {
                            return (
                                <Box key={index} marginBottom={1}>
                                    {signature.user.name}
                                </Box>
                            );
                        });
                },
            },
        ];

        return (
            <Box className={styles.BlockDetail}>
                <Box className={styles.BlockDetail__item} marginBottom={1}>
                    <Box>Name:</Box>
                    <Box>{delegationName}</Box>
                </Box>
                {orderDisplayData.map((orderItem, index) => {
                    return (
                        <Conditional
                            condition={!lodash.isEmpty(lodash.get(data, orderItem.key))}
                            key={index}
                        >
                            <Box className={styles.BlockDetail__item}>
                                <Box>{orderItem.label}</Box>
                                <Box>
                                    {lodash.isFunction(orderItem.render) ? (
                                        orderItem.render()
                                    ) : (
                                        <Box marginBottom={1}>
                                            {lodash.get(data, orderItem.key)}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Conditional>
                    );
                })}
            </Box>
        );
    };

    const renderModifyAudit = () => {
        const orderDisplayData = [
            {
                label: 'ROLE:',
                key: 'roleName',
                render: (key) => {
                    return data[key].roleName.map((name, index) => (
                        <Box key={index} marginBottom={1}>
                            {name}
                        </Box>
                    ));
                },
            },
            {
                label: 'TASKS:',
                key: 'tasks',
                render: (key) => {
                    return data[key].tasks.map((task, index) => (
                        <Box key={index} marginBottom={1}>
                            {task.taskName}
                        </Box>
                    ));
                },
            },
            {
                label: 'START DATE:',
                key: 'startDate',
                render: (key) => {
                    return (
                        <Box marginBottom={1}>
                            {parseDateFormat(data[key].startDate[0])}
                        </Box>
                    );
                },
            },
            {
                label: 'SIGNATURES:',
                key: 'signatureExpectations',
                render: (key) => {
                    return lodash
                        .uniqBy(data[key].signatureExpectations, 'user.id')
                        .map((originalSignature, index) => {
                            return (
                                <Box key={index} marginBottom={1}>
                                    {originalSignature.user.name}
                                </Box>
                            );
                        });
                },
            },
            {
                label: 'END DATE:',
                key: 'endDate',
                render: (key) => {
                    return (
                        <Box marginBottom={1}>
                            {parseDateFormat(data[key].endDate[0])}
                        </Box>
                    );
                },
            },
            {
                label: 'Expectation:',
                key: 'originalSignature',
                render: (key) => {
                    return data[key].originalSignature.map((signature, index) => {
                        return (
                            <Box key={index} marginBottom={1}>
                                {signature.user.name}
                            </Box>
                        );
                    });
                },
            },
            {
                label: 'Recipient:',
                key: 'requestedSignature',
                render: (key) => {
                    return lodash
                        .uniqBy(data[key].requestedSignature, 'user.name')
                        .map((signature, index) => {
                            return (
                                <Box key={index} marginBottom={1}>
                                    {signature.user.name}
                                </Box>
                            );
                        });
                },
            },
        ];

        const defaultFieldsModify = [
            {
                label: 'Removed',
                key: 'removed',
            },
            {
                label: 'Added',
                key: 'added',
            },
            {
                label: 'Original Signatures',
                key: 'originalSignature',
            },
            {
                label: 'Requested Signatures',
                key: 'requestedSignature',
            },
        ];

        const fieldsHaveModify = defaultFieldsModify.filter(
            (item) => !lodash.isEmpty(lodash.get(data, item.key))
        );

        return (
            <Box className={styles.BlockDetail}>
                {fieldsHaveModify.map((field, index) => {
                    if (!lodash.get(data, field.key, false)) return;

                    return (
                        <Box key={index}>
                            <Box className={styles.BlockDetail__action}>{field.label}</Box>
                            <Box>
                                {orderDisplayData.map((orderItem, index) => {
                                    if (
                                        !Object.keys(lodash.get(data, field.key)).includes(
                                            orderItem.key
                                        )
                                    )
                                        return null;

                                    return (
                                        <Box className={styles.BlockDetail__item} key={index}>
                                            <Box style={{ minWidth: '140px', marginLeft: 8 }}>
                                                {orderItem.label}
                                            </Box>
                                            <Box>{orderItem.render(field.key)}</Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderTimezone = (date) => {
        const formatDate = replaceTimezone(toISOLocalWithTz(new Date(date), institutionTimezone));
        const tzAbbreviation = getTzAbbreviation(date, institutionTimezoneName);

        return (
            <Box>
                {moment(formatDate).format('hh:mm A')}
                <span> - {tzAbbreviation}</span>
            </Box>
        );
    };

    if (!data) return null;

    return (
        <div className={styles.AuditHistoryItem}>
            <Box className={styles.BlockTitle}>
                <Box className={styles.BlockTitle__leftSide}>{renderTimezone(data.createdAt)}</Box>

                <Box className={styles.BlockTitle__rightSide}>
                    <span style={{ maxWidth: 175 }}>{data.actionUserDisplayName}</span>

                    <Box className={styles.BlockTitle__control}>
                        <div>{lodash.get(data, 'label', '')}</div>
                        <ExpandMoreIcon
                            className={clsx(classes.expand, {
                                [classes.expandOpen]: expanded,
                            })}
                            expand={expanded.toString()}
                            onClick={handleExpandClick}
                            aria-expanded={expanded}
                            aria-label="show audit more"
                        />
                    </Box>
                </Box>
            </Box>

            <Collapse
                in={expanded}
                timeout="auto"
                addEndListener={(e) => {
                    setTimeout(() => {
                        scrollIntoView(e);
                    }, 100);
                }}
            >
                {/* Support study type */}
                <Conditional condition={isStudyType}>
                    <Box className={styles.BlockSubTitle}>
                        <Box>Origin</Box>
                        <Box>{getOriginDisplay(data)}</Box>
                    </Box>
                </Conditional>

                <Conditional condition={isStudyType && !!getActivityDisplay(data)}>
                    <Box className={styles.BlockSubTitle}>
                        <Box>Activity</Box>
                        <Box style={{ paddingRight: 27 }}>
                            {getActivityDisplay(data)}
                        </Box>
                    </Box>
                </Conditional>

                <Conditional
                    condition={data.actionType.includes('SIGN')}
                    className={styles.BlockSubTitle}
                >
                    <Box className={styles.BlockSubTitle}>
                        <Box>Testament</Box>
                        <Box style={{ paddingRight: 30 }}>{testament}</Box>
                    </Box>
                </Conditional>

                <Conditional
                    condition={
                        data.actionType.includes('EDIT_DELEGATION') &&
                        lodash.get(data, 'notes') !== null &&
                        lodash.get(data, 'notes').length > 0
                    }
                >
                    <Box className={styles.BlockSubTitle}>
                        <Box>Explanation</Box>
                        <Box style={{ paddingRight: 30 }}>{data.notes}</Box>
                    </Box>
                </Conditional>

                <Box>
                    <Box className={styles.TitleAudit} data-testid="AuditItem__Detail">
                        Details
                    </Box>
                    {data.actionType.includes('EDIT')
                        ? renderModifyAudit()
                        : renderCreateAndSignAudit()}
                </Box>
            </Collapse>
            <Divider style={{ width: 495 }} />
        </div>
    );
}
